import { ApiError } from '@/lib/apiClient';

const base = import.meta.env.VITE_API_BASE ?? '';

export interface UploadedAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  isInline: boolean;
  url: string;
}

/**
 * Attachment upload/download. Uses raw fetch (not apiClient) because uploads are
 * multipart/form-data and downloads return binary blobs — both bypass the JSON
 * wrapper. The bearer token is attached the same way apiClient does.
 */
export const attachmentsApi = {
  /** POST /api/attachments (multipart). `inline` marks pasted/embedded images. */
  upload: async (
    file: File,
    inline: boolean,
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<UploadedAttachment> => {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('inline', String(inline));

    const res = await fetch(`${base}/api/attachments`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
      body: form,
      signal
    });

    const text = await res.text();
    const body = text ? safeJson(text) : undefined;
    if (!res.ok) throw new ApiError(res.status, `upload → ${res.status}`, body ?? text);
    return body as UploadedAttachment;
  },

  /** GET /api/attachments/{id} → Blob (authorized). */
  fetchBlob: async (
    id: string,
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<Blob> => {
    const res = await fetch(`${base}/api/attachments/${id}`, {
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
      signal
    });
    if (!res.ok) throw new ApiError(res.status, `download → ${res.status}`);
    return res.blob();
  }
};

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
